/**
 * The autoplay run as an ordered step list, from which `transitionManager` builds one parent timeline.
 *
 * At every boundary the app is either fading or holding, never momentarily at rest. See context/autoplay.md.
 */
import { selectPlayback, selectPlaylist } from './selectors'

/**
 * Steps for one lap from `current_preset_index`.
 *
 * ⚠️ `loop: true` plans ONE lap, not an unbounded parent — the run re-plans on landing. `loop: false` ends after the last preset, where the run stops autoplay.
 *
 * @returns `{ steps }`, or `{ steps: [], refused }` naming why nothing can run.
 */
export const planRun = state => {
	const { current_preset_index, hold_duration, fade_duration, loop } = selectPlayback(state)
	const presets = selectPlaylist(state)

	if (presets.length < 2) return { steps: [], refused: 'not enough presets' }

	const holdMs = hold_duration * 1000
	const fadeMs = fade_duration * 1000

	// ⚠️ A parent of only zero-duration children collapses to `duration: 0` and fires ZERO onStart callbacks — it can never advance. Refused here so autoplay declines to start rather than dying silently.
	if (!holdMs && !fadeMs) return { steps: [], refused: 'zero fade and zero hold' }

	const steps = []
	let from = current_preset_index
	// A run with no current preset starts at 0; the load that puts it there precedes the plan and is not a step.
	if (from < 0) from = 0
	// And one past the end starts at the last: a delete can leave the index out of range, and an unguarded `from` emits a first step naming no preset.
	if (from >= presets.length) from = presets.length - 1

	// ONE lap: `presets.length` steps is exactly a full circuit from wherever it starts, so a looping run closes on its origin and re-plans there.
	for (let n = 0; n < presets.length; n++) {
		// A hold precedes the fade it holds BEFORE. Zero means no child at all, never a zero-duration one.
		//
		// ⚠️ `cut` lands a zero-`fade_duration` advance at this hold's own `onStart`: a spanless advance can be no child, since GSAP never positions an empty one.
		if (holdMs) steps.push({ kind: 'hold', at: from, ms: holdMs, ...(fadeMs || n === 0 ? {} : { cut: from }) })

		const next = from + 1
		if (next >= presets.length && !loop) break
		const to = next >= presets.length ? 0 : next

		if (fadeMs) steps.push({ kind: 'fade', from, to, ms: fadeMs })
		from = to
	}

	if (!steps.length) return { steps: [], refused: 'nothing to advance to' }
	return { steps }
}
